import React, {createContext, type Dispatch, useContext, useState} from 'react';
import {shallowEqual} from 'react-redux';
import {useSyncExternalStore} from 'use-sync-external-store/shim';
import {useSyncExternalStoreWithSelector} from 'use-sync-external-store/shim/with-selector';

import type {AgentChatAction} from './actions';
import {
  type ActiveThreadLoadState,
  selectActiveThread,
  selectActiveThreadLoadState,
} from './selectors';
import {type AgentChatStore, createAgentChatStore} from './store';
import type {AgentChatState, Thread} from './thread';

export type {AgentChatStore};
export type AgentChatDispatch = Dispatch<AgentChatAction>;

// Context

const AgentChatStoreContext = createContext<AgentChatStore | null>(null);

/**
 * useAgentChatStore returns the store handle for non-subscribing access
 * (e.g. reading state inside callbacks). Subscribing consumers should use
 * useAgentChatState, useStoreSelector, or a named selector hook.
 */
export function useAgentChatStore(): AgentChatStore {
  const store = useContext(AgentChatStoreContext);
  if (store == null) {
    throw new Error(
      'useAgentChatStore must be used inside an AgentChatStoreProvider'
    );
  }

  return store;
}

// Hooks

export function useAgentChatDispatch(): Dispatch<AgentChatAction> {
  return useAgentChatStore().dispatch;
}

/**
 * useAgentChatState subscribes to the entire store and re-renders on every
 * dispatch. Prefer a narrower hook unless the whole state is genuinely needed.
 */
export function useAgentChatState(): AgentChatState {
  const store = useAgentChatStore();
  return useSyncExternalStore(store.subscribe, store.getState);
}

/**
 * useStoreSelector subscribes to a derived slice, re-rendering when the
 * selected value changes (compared via strict equality or, if provided,
 * `isEqual`).
 */
export function useStoreSelector<T>(
  selector: (state: AgentChatState) => T,
  isEqual?: (a: T, b: T) => boolean
): T {
  const store = useAgentChatStore();
  return useSyncExternalStoreWithSelector(
    store.subscribe,
    store.getState,
    null,
    selector,
    isEqual
  );
}

export function useActiveThread(): Thread | undefined {
  return useStoreSelector(selectActiveThread);
}

export function useActiveThreadLoadState(): ActiveThreadLoadState {
  return useStoreSelector(selectActiveThreadLoadState, shallowEqual);
}

// Provider

export function AgentChatStoreProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [store] = useState(createAgentChatStore);
  return (
    <AgentChatStoreContext.Provider value={store}>
      {children}
    </AgentChatStoreContext.Provider>
  );
}
